export const STATUS_META = {
  pending: { label: "Pending review", color: "bg-amber-100 text-amber-800 border-amber-300" },
  active: { label: "Published", color: "bg-green-100 text-green-900 border-green-400" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700 border-red-300" },
  awaiting_partner: { label: "Awaiting partner", color: "bg-amber-100 text-amber-800 border-amber-300" },
  matched: { label: "Matched", color: "bg-blue-100 text-blue-800 border-blue-300" },
  negotiating: { label: "Negotiating fee", color: "bg-blue-100 text-blue-800 border-blue-300" },
  fee_agreed: { label: "Fee agreed", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  paid: { label: "Fee held — awaiting code", color: "bg-violet-100 text-violet-800 border-violet-300" },
  code_shared: { label: "Code shared", color: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  code_confirmed: { label: "Confirmed — payout pending", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  completed: { label: "Completed", color: "bg-green-100 text-green-900 border-green-400" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 border-red-300" },
  expired: { label: "Hold expired", color: "bg-red-100 text-red-700 border-red-300" },
};

export function StatusBadge({ status, testid }) {
  const meta = STATUS_META[status] || { label: status, color: "bg-secondary text-foreground border-black/10" };
  return (
    <span
      data-testid={testid || `status-${status}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}
    >
      {meta.label}
    </span>
  );
}

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const priceLabel = (deal, field = "original_price") =>
  deal?.price_type === "tbc" ? "To be calculated" : money(deal?.[field]);

export const locationLabel = (deal) => {
  if (!deal) return "";
  if (deal.location_scope === "city") return [deal.city, deal.province].filter(Boolean).join(", ") || "City";
  if (deal.location_scope === "province") return deal.province || "Province-wide";
  return "Canada-wide";
};

// Classify a deal as a free vs discounted second item, or a multi-pack split (label first, then price gap).
export const dealScheme = (deal) => {
  const dt = String(deal?.deal_type || "").toLowerCase();
  if (dt.includes("multi-pack") || dt.includes("multipack") || dt.includes("multi pack") || dt.includes("bulk")) return "multipack";
  if (dt.includes("free")) return "free";
  if (dt.includes("discount") || dt.includes("half") || dt.includes("%") || dt.includes("off")) return "discount";
  const o = Number(deal?.original_price || 0), p = Number(deal?.deal_price || 0);
  if (o > 0 && p > 0 && p < o) return "discount";
  return "free";
};

// A shopper's savings, obtained per person.
//  • free      → split one item's cost, so you save half its full price
//  • discount  → the amount knocked off (original − discounted)
//  • multipack → what you save vs buying the whole pack alone (pack price − your share)
export const dealSavings = (deal) => {
  const o = Number(deal?.original_price || 0);
  const p = Number(deal?.deal_price || 0);
  const s = dealScheme(deal);
  if (s === "discount" || s === "multipack") {
    const gap = Math.max(o - p, 0);
    return gap > 0 ? gap : (o || p) / 2;
  }
  return (o || p) / 2;
};

// Compact coloured badge to tell deal types apart at a glance.
export const schemeBadge = (deal) => {
  const s = dealScheme(deal);
  if (s === "free") return { text: "Free", cls: "bg-green-600 text-white" };
  if (s === "multipack") return { text: "Multi-pack", cls: "bg-blue-600 text-white" };
  const o = Number(deal?.original_price || 0), p = Number(deal?.deal_price || 0);
  const m = String(deal?.deal_type || "").match(/(\d+)\s*%/);
  let pct = m ? Number(m[1]) : (o > 0 && p > 0 ? Math.round((1 - p / o) * 100) : null);
  return { text: pct ? `${pct}% off` : "Discount", cls: "bg-orange-500 text-white" };
};

// Days until a deal's flyer offer ends (null if no/invalid date). Negative = already ended.
export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

export const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr).slice(0, 10);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
};


